import type { MouseEvent } from "react";

import type { FloorplanDevice } from "./types";

/** Role glyphs: a router anchors the network, an AP radiates coverage, a
 *  switch switches. The label under each marker is always the device name. */
const ROLE_GLYPH: Record<string, string> = {
  Router: "📶",
  "Access Point": "📡",
  "Access Switch": "🔀",
};
const ROLE_COLOR: Record<string, string> = {
  Router: "#f59e0b",
  "Access Point": "#10b981",
  "Access Switch": "#6366f1",
};

export interface DeviceOverlayProps {
  devices?: FloorplanDevice[];
  onDeviceClick?: (device: FloorplanDevice) => void;
}

/**
 * Placed crm.Device markers (AP / switch / router). Point geometry renders a
 * glyph + label; Polygon geometry renders the footprint with a centered
 * glyph. Devices whose role is not in the layer's filter never reach this
 * component (delivery is role-gated server-side), and unplaced devices are
 * not delivered at all — no need to re-filter here.
 */
export function DeviceOverlay({ devices, onDeviceClick }: DeviceOverlayProps) {
  if (!devices || devices.length === 0) return null;

  const renderItem = (d: FloorplanDevice) => {
    const g = d.geometry;
    if (!g) return null;
    const glyph = ROLE_GLYPH[d.role] ?? "💻";
    const color = ROLE_COLOR[d.role] ?? "#64748b";
    const label = d.label || d.deviceName;
    const key = `device-${d.id}`;
    const cursor = onDeviceClick ? { cursor: "pointer" } : { cursor: "default" };
    const onClick = onDeviceClick
      ? (e: MouseEvent<SVGGElement>) => {
          e.stopPropagation();
          onDeviceClick(d);
        }
      : undefined;

    if (g.type === "Point") {
      const [cx, cy] = g.coordinates;
      if (cx == null || cy == null) return null;
      return (
        <g key={key} style={cursor} onClick={onClick}>
          <circle cx={cx} cy={cy} r={11} fill="white" stroke={color} strokeWidth={2} />
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={11} style={{ pointerEvents: "none" }}>
            {glyph}
          </text>
          {label && (
            <text x={cx} y={cy + 22} textAnchor="middle" fontSize={9} fontWeight={600} fill="#334155" style={{ pointerEvents: "none" }}>
              {label}
            </text>
          )}
        </g>
      );
    }

    // Polygon / MultiPolygon footprint.
    const polys: number[][][] =
      g.type === "Polygon" ? [g.coordinates?.[0] ?? []] : g.type === "MultiPolygon" ? (g.coordinates ?? []).map((p) => p?.[0] ?? []) : [];
    return polys.map((poly, pi) => {
      if (!poly || poly.length < 3) return null;
      const pts = poly.map((p) => p.join(",")).join(" ");
      const xs = poly.map((p) => p[0]);
      const ys = poly.map((p) => p[1]);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
      return (
        <g key={`${key}-${pi}`} style={cursor} onClick={onClick}>
          <polygon points={pts} fill={color} fillOpacity={0.16} stroke={color} strokeWidth={2.5} strokeDasharray="5,3" />
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={13} style={{ pointerEvents: "none" }}>
            {glyph}
          </text>
        </g>
      );
    });
  };

  return <g>{devices.flatMap((d) => renderItem(d))}</g>;
}
